import { createReducer, on } from '@ngrx/store';
import { AiInsightsFeatureState, AiInsightsInitialState } from './ai-insights-state';
import { AiInsightsChatRequestActions, AiInsightsChatSessionActions, AiInsightsChatSuggestionsActions } from './ai-insights-actions';
import { ChatMessage, ChatMessageRole, ChatMessageType } from '../../../models/ai-insights/chat-message';
import { ChatExchange } from '../../../models/ai-insights/chat-exchange';
import { sessionEntityAdapter, sessionsEntityAdapter } from './ai-insights-adapters';

export const aiInsightsReducer = createReducer(AiInsightsInitialState,
    on(AiInsightsChatRequestActions.requestChat, (state, {
      chatRequest, chatExchangeId, timestamp,
    }): AiInsightsFeatureState => {
      const question: ChatMessage = {
        type: ChatMessageType.TEXT,
        role: ChatMessageRole.USER,
        content: chatRequest.message,
      };

      const chatExchange: ChatExchange = {
        id: chatExchangeId,
        question: {
          timestamp,
          body: question,
        },
        answer: {
          body: [],
        },
      };

      return {
        ...state,
        session: sessionEntityAdapter.upsertOne(chatExchange, state.session),
      };
    }),
    on(AiInsightsChatRequestActions.requestChatSuccess,
        AiInsightsChatRequestActions.requestChatError,
        (state, {
          chatExchange,
        }): AiInsightsFeatureState => {
          return {
            ...state,
            session: sessionEntityAdapter.upsertOne(chatExchange, state.session),
          };
        }),
    on(AiInsightsChatSessionActions.startNewChatSession,
        AiInsightsChatSessionActions.selectExistingChatSession, (state, {
          sessionId,
        }): AiInsightsFeatureState => {
          return {
            ...state,
            sessionId,
            // reset convo data
            session: sessionEntityAdapter.getInitialState(),
            sessionTotalPages: null,
            sessionPage: null,
          };
        }),
    on(AiInsightsChatSuggestionsActions.getSuggestionsSuccess, (state, {
      chatSuggestions,
    }): AiInsightsFeatureState => {
      return {
        ...state,
        chatSuggestions,
      };
    }),
    on(AiInsightsChatSessionActions.getChatSessionsSuccess, (state, {
      sessions,
    }): AiInsightsFeatureState => {
      // If current page is now out-of-bounds due to deletions, snap it back.
      const currentPage = (state.sessionsPage ?? 1) > sessions.total_pages
          ? sessions.total_pages
          : (state.sessionsPage ?? 1);
      return {
        ...state,
        sessions: sessionsEntityAdapter.upsertMany(sessions.sessions, state.sessions),
        // stay on current page if it's bigger than the requested page (we refresh page 1
        // after creating / deleting a session)
        sessionsPage: Math.max(currentPage, sessions.page),
        sessionsTotalPages: sessions.total_pages,
      };
    }),
    on(AiInsightsChatSessionActions.renameChatSessionSuccess, (state, {
      session,
    }): AiInsightsFeatureState => {
      return {
        ...state,
        sessions: sessionsEntityAdapter.upsertOne(session, state.sessions),
      };
    }),
    on(AiInsightsChatSessionActions.deleteChatSessionSuccess, (state, {
      sessionId,
    }): AiInsightsFeatureState => {
      return {
        ...state,
        sessions: sessionsEntityAdapter.removeOne(sessionId, state.sessions),
      };
    }),
    on(AiInsightsChatSessionActions.getChatSessionDetailSuccess, (state, {
      sessionDetail, page, totalPages,
    }): AiInsightsFeatureState => {
      return {
        ...state,
        session: sessionEntityAdapter.upsertMany(sessionDetail, state.session),
        sessionPage: page,
        sessionTotalPages: totalPages,
      };
    }),
);

